import { useCallback, useEffect, useRef, useState } from 'react';
import { useSocket } from 'socket.io-react-hook';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { _getToken } from 'src/Config/axiosApi';
import { refreshAuthToken } from 'src/Common/context/AuthContext';
import { NotificationEvent } from '../types/notifications.types';
import { compareStreamIds } from '../utils/notificationsFeed';

// Re-exported for existing importers that pull it from this hook; the
// canonical definition now lives in notificationsFeed (alongside the feed
// sort comparator that also uses it).
export { compareStreamIds };

/**
 * Seconds until a JWT's `exp`, or null if it can't be decoded. Used to
 * schedule a proactive refresh before the gateway disconnects the
 * socket for an expired token. Local copy (AuthContext's helper is not
 * exported) — only reads the standard `exp` claim.
 */
function secondsUntilTokenExpiry(token: string | null): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
    ) as { exp?: number };
    if (!payload?.exp) return null;
    return payload.exp - Math.floor(Date.now() / 1000);
  } catch {
    return null;
  }
}

/**
 * Refresh the token this many seconds BEFORE it expires so a fresh
 * token is in storage before the gateway would reject the socket.
 */
const PROACTIVE_REFRESH_LEAD_SECONDS = 60;

/**
 * Cooldown between auto-refresh attempts on a connect/auth error. A
 * stale token that keeps getting rejected on every reconnect attempt
 * would otherwise hammer `/auth/refresh`. socket.io owns the reconnect
 * loop (infinite, backed off); this only throttles how often WE force a
 * token refresh in response to an auth error — the socket keeps
 * retrying regardless.
 */
const AUTH_RECOVERY_COOLDOWN_MS = 10_000;

/**
 * What the page consumes from this hook. A `drain()` returns and clears
 * the queue of every `notification` frame received since the last
 * drain — NO frame is overwritten (the old single-slot `latestEvent`
 * dropped same-tick bursts). `queueVersion` bumps on every enqueue so a
 * consumer effect re-runs and drains.
 */
export interface NotificationsSocketApi {
  isConnected: boolean;
  /** Bumps each time one or more frames are enqueued — drain trigger. */
  queueVersion: number;
  /** Returns + clears every queued `NotificationEvent` (FIFO order). */
  drain: () => NotificationEvent[];
  bufferedCount: number;
  resetBufferedCount: () => void;
  /** Id of the last applied stream entry (`<ms>-<seq>`), '' if none. */
  lastStreamId: string;
  /** True between a (re)connect and its `backfill_complete`. */
  isCatchingUp: boolean;
  /**
   * Bumps when the FE detects a tail gap it cannot self-heal from the
   * live stream (the next reconnect's replay closes it). The page may
   * use this as a coarse REST top-up trigger. Also bumps when the
   * server signals a possibly-incomplete replay via `backfill_complete`.
   */
  gapVersion: number;
  /**
   * Legacy single-slot surface kept ONLY for the social-notifications
   * sidebar bell (`useSocialNotifications.ts`), which is out of scope for
   * this redesign and consumes events one-at-a-time deduped by id. It is
   * a thin DERIVED view over the durable queue (the queue is the source
   * of truth). New consumers MUST use `drain`/`queueVersion`. `bufferedCount`
   * above doubles as the old badge counter. `reconnectCount` bumps on
   * every disconnect→connect transition (its only use is the social
   * bell's REST top-up on reconnect).
   */
  latestEvent: NotificationEvent | null;
  acknowledge: () => void;
  reconnectCount: number;
}

/**
 * Connects to the notifications-websocket service and exposes a durable,
 * cursor-based stream of `NotificationEvent`s.
 *
 * Reliability model (durable Redis Stream + cursor replay):
 *  - The relay forwards each `notification` frame WITH a `streamId`
 *    (the Redis Stream entry id `<ms>-<seq>`). We track the id of the
 *    most-recently-applied event in `lastStreamIdRef`.
 *  - On EVERY (re)connect we emit `subscribe_notifications` with that
 *    cursor; the gateway replays everything since it (`XRANGE`) before
 *    resuming live, then emits `backfill_complete { upTo }`. So a
 *    disconnect — gateway-pod restart, tab sleep, network blip — is
 *    recovered with no page refresh and no gap.
 *  - Frames are pushed into a QUEUE (never a single slot) so two frames
 *    delivered in the same React tick are both seen by the page.
 *
 * Auth: socket.io owns an infinite, backed-off reconnect loop. We
 * proactively refresh the JWT before `exp` and, on `connect_error` /
 * `auth_error`, refresh-and-retry without ever giving up — the socket
 * never stays permanently dead. `clearOnFailure:false` keeps a WS blip
 * from logging the operator out.
 *
 * Connection model: separate Socket.io connection from `/unwind`
 * (different host) — by design, so notification fanout can't head-of-
 * line block the market-data WS.
 */
export function useNotificationsSocket(): NotificationsSocketApi {
  const wsUrl = import.meta.env.VITE_NOTIFICATIONS_WS_URL;
  const accessToken = _getToken();

  const { socket } = useSocket(wsUrl || '', {
    enabled: !!wsUrl && !!accessToken,
    transports: ['websocket'],
    // Infinite, capped-exponential reconnection — the socket must never
    // give up. A failed JWT refresh does NOT stop this loop; we just
    // keep retrying until a fresh token lets the handshake through.
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 30_000,
    auth: (cb: (data: { token: string }) => void) =>
      cb({ token: _getToken() }),
  });

  // Ref-backed queue of received frames + a version counter so the page
  // re-runs its drain effect. We never store events in a single state
  // slot (that dropped same-tick bursts).
  const queueRef = useRef<NotificationEvent[]>([]);
  const [queueVersion, setQueueVersion] = useState(0);
  const [bufferedCount, setBufferedCount] = useState(0);

  // FE replay cursor — the id of the most-recently-applied stream entry.
  // Empty string means "never applied one" → first subscribe omits it so
  // the server seeds the client live.
  const lastStreamIdRef = useRef<string>('');
  const [lastStreamId, setLastStreamId] = useState<string>('');

  const [isCatchingUp, setIsCatchingUp] = useState(false);
  const [gapVersion, setGapVersion] = useState(0);

  // Legacy single-slot surface for the out-of-scope social bell only —
  // see `NotificationsSocketApi`. Driven off the SAME frame intake below.
  const [latestEvent, setLatestEvent] = useState<NotificationEvent | null>(
    null,
  );
  const [reconnectCount, setReconnectCount] = useState(0);
  const hasDisconnected = useRef(false);

  // Last time we auto-refreshed the token in response to a connect/auth
  // error — throttles the refresh so a persistently-rejected token can't
  // loop on `/auth/refresh`. (socket.io still retries the connection.)
  const lastAuthRecoveryAt = useRef(0);

  // ── Frame intake: queue every `notification`, advance the cursor ──────
  //
  // We subscribe to the raw socket event (NOT `useSocketEvent`, which
  // only exposes the LATEST message) so two frames in the same tick are
  // BOTH enqueued — no overwrite. Each frame carries its `streamId`; an id
  // strictly greater than the cursor advances it, an id that is not is a
  // duplicate (typically replay overlapping live) — still enqueued, but the
  // cursor never rewinds, and the page merge dedupes by `id`. Trim-induced
  // tail gaps are surfaced separately via `backfill_complete { gap }`.
  useEffect(() => {
    const enqueue = (event: NotificationEvent) => {
      queueRef.current.push(event);
      setBufferedCount((n) => n + 1);
      setQueueVersion((n) => n + 1);
      setLatestEvent(event); // legacy single-slot view (social bell only)
    };
    const onNotification = (event: NotificationEvent) => {
      const incomingId = event.streamId;
      if (incomingId) {
        const cursor = lastStreamIdRef.current;
        const cmp = cursor ? compareStreamIds(incomingId, cursor) : 1;
        if (cmp <= 0) {
          // Not strictly greater than the cursor → duplicate (typically
          // replay overlapping live). The page merge dedupes by `id`, so
          // we still enqueue but do NOT rewind the cursor.
          enqueue(event);
          return;
        }
        lastStreamIdRef.current = incomingId;
        setLastStreamId(incomingId);
      }
      enqueue(event);
    };

    socket.on('notification', onNotification);
    return () => {
      socket.off('notification', onNotification);
    };
  }, [socket]);

  // ── Replay handshake: subscribe with the cursor on every (re)connect ──
  useEffect(() => {
    const subscribe = () => {
      const cursor = lastStreamIdRef.current;
      setIsCatchingUp(true);
      socket.emit(
        'subscribe_notifications',
        cursor ? { lastStreamId: cursor } : {},
      );
      if (hasDisconnected.current) {
        // Genuine reconnect (not the first connect after mount) — bump
        // for the out-of-scope social bell's REST top-up.
        hasDisconnected.current = false;
        setReconnectCount((n) => n + 1);
      }
    };
    const onDisconnect = () => {
      hasDisconnected.current = true;
    };

    const onBackfillComplete = (data?: { upTo?: string; gap?: boolean }) => {
      setIsCatchingUp(false);
      const upTo = data?.upTo;
      // The server sets `gap: true` when our cursor was trimmed past the
      // stream floor — the authoritative signal, since the replayed frames
      // advance our cursor PAST `lastStreamId` and a FE-only `upTo < cursor`
      // check can never observe the trim. We also keep the `upTo < cursor`
      // check as a defensive secondary (older gateways without the flag).
      const cursorTrimmed = data?.gap === true;
      const upToBelowCursor =
        !!upTo &&
        !!lastStreamIdRef.current &&
        compareStreamIds(upTo, lastStreamIdRef.current) < 0;
      if (cursorTrimmed || upToBelowCursor) {
        Sentry.captureMessage(
          'notifications WS replay incomplete — cursor older than stream floor',
          { level: 'warning', tags: { area: 'notifications-ws' } },
        );
        setGapVersion((n) => n + 1);
      }
    };

    socket.on('connect', subscribe);
    socket.on('disconnect', onDisconnect);
    socket.on('backfill_complete', onBackfillComplete);
    if (socket.connected) subscribe();
    else hasDisconnected.current = true;

    return () => {
      socket.off('connect', subscribe);
      socket.off('disconnect', onDisconnect);
      socket.off('backfill_complete', onBackfillComplete);
    };
  }, [socket, socket.connected]);

  // ── Proactive token refresh before `exp` ──────────────────────────────
  //
  // The gateway disconnects a socket whose token expired. Refreshing
  // ahead of `exp` means the `auth` callback always re-reads a still-
  // valid token on the next (re)connect, so we rarely hit an auth error
  // at all. Re-schedules itself whenever the token changes.
  useEffect(() => {
    const ttl = secondsUntilTokenExpiry(accessToken);
    if (ttl === null) return;
    const delayMs = Math.max(0, (ttl - PROACTIVE_REFRESH_LEAD_SECONDS) * 1000);
    const timer = setTimeout(() => {
      refreshAuthToken({ clearOnFailure: false }).catch((err) => {
        Sentry.captureException(err, { tags: { area: 'notifications-ws' } });
      });
    }, delayMs);
    return () => clearTimeout(timer);
  }, [accessToken]);

  // ── Auth-error recovery: refresh-and-retry, NEVER give up ─────────────
  //
  // socket.io keeps reconnecting on its own (infinite + backoff). On an
  // auth error we additionally refresh the token (once per cooldown) so
  // the next automatic reconnect carries a fresh one. We never stop the
  // loop and never log the operator out (`clearOnFailure:false`).
  useEffect(() => {
    const handleAuthError = async (err?: unknown) => {
      const now = Date.now();
      if (now - lastAuthRecoveryAt.current < AUTH_RECOVERY_COOLDOWN_MS) {
        Sentry.captureException(
          err instanceof Error ? err : new Error('notifications WS auth error'),
          { tags: { area: 'notifications-ws' } },
        );
        return;
      }
      lastAuthRecoveryAt.current = now;

      let newToken: string | null = null;
      try {
        // `clearOnFailure: false` — a notifications-WS problem must NEVER
        // wipe the primary session. On refresh failure we stay silent
        // and let socket.io keep retrying; the next API 401 owns the
        // destructive logout decision.
        newToken = await refreshAuthToken({ clearOnFailure: false });
      } catch (refreshErr) {
        Sentry.captureException(refreshErr, {
          tags: { area: 'notifications-ws' },
        });
      }

      if (newToken) {
        // Nudge an immediate reconnect with the fresh token instead of
        // waiting out the backoff. `connect()` is a no-op if already
        // connecting.
        try {
          socket.disconnect();
        } catch {
          /* already closed */
        }
        socket.connect();
      } else {
        // Refresh failed — do NOT give up. socket.io's own infinite
        // reconnect loop keeps trying; a later proactive/manual refresh
        // (or the operator re-auth) will let the handshake through. We
        // surface a single toast so the operator knows the feed is
        // catching up, never a permanently-dead socket.
        toast.error(
          'Notifications reconnecting — will recover automatically.',
          { toastId: 'notifications-ws-auth' },
        );
        Sentry.captureException(
          err instanceof Error
            ? err
            : new Error('notifications WS auth error — refresh failed'),
          { tags: { area: 'notifications-ws' } },
        );
      }
    };

    socket.on('connect_error', handleAuthError);
    socket.on('auth_error', handleAuthError);
    return () => {
      socket.off('connect_error', handleAuthError);
      socket.off('auth_error', handleAuthError);
    };
  }, [socket]);

  /** Returns + clears every queued frame in FIFO order. */
  const drain = useCallback((): NotificationEvent[] => {
    if (queueRef.current.length === 0) return [];
    const batch = queueRef.current;
    queueRef.current = [];
    return batch;
  }, []);

  /** Reset the buffered count badge (e.g. after the page refocuses). */
  const resetBufferedCount = useCallback(() => {
    setBufferedCount(0);
  }, []);

  /** Legacy: clear the single-slot view after the social bell consumed it. */
  const acknowledge = useCallback(() => {
    setLatestEvent(null);
  }, []);

  return {
    isConnected: socket.connected,
    queueVersion,
    drain,
    bufferedCount,
    resetBufferedCount,
    lastStreamId,
    isCatchingUp,
    gapVersion,
    latestEvent,
    acknowledge,
    reconnectCount,
  };
}
